// Library imports
import axios from 'axios';
import auth0 from 'auth0-js';
import _ from 'lodash';

// Project imports
import * as types from './actionTypes';
import * as urls from './urlTypes';

import { saveTrackingData, saveUerState } from '../util/trackerUtils';
import {
  getClonedPrefrences,
  setModuleDataOnSave,
} from '../util/preferenceUtils';

import {
  setAccountDetail,
  setActiveAccount,
  setPerferedAccount,
} from './accountActions';
import { toastError } from '../util/toastUtils';

export function loginSuccess(loginResponse) {
  return {
    type: types.LOGIN_SUCCESS,
    payload: loginResponse,
  };
}

export function saveFeedback(
  token,
  uid,
  accountId,
  feedback,
  section,
  comments
) {
  const url = `${urls.ROOT_URL}/users/feedback/${uid}/${accountId}`;
  return () => {
    axios({
      url,
      method: 'post',
      withCredentials: true,
      responseType: 'json',
      data: {
        type: 'feedback',
        feedback,
        section,
        comments,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(response => {
        console.log(response);
      })
      .catch(response => {
        console.warn(response);
      });
  };
}

export const getUserFeatureDetails = async (uid, accountId) => {
  const url = `${urls.ROOT_URL}/users/${uid}?accountId=${accountId}`;
  try {
    const response = await axios({
      url,
      method: 'get',
      responseType: 'json',
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.warn(`Initial Account call failed: ${error}`);
    return false;
  }
};

const getActiveUserDetail = async () => {
  const url = `${urls.API_GATEWAY_URL}/session/user`;
  const statusResponse = await getUserStatus();
  
  if(statusResponse && statusResponse.data === urls.LOGGED_IN){
    try {
      const response = await axios({
        url,
        method: 'get',
        withCredentials: true,
        responseType: 'json',
      });
  
      const responseType = typeof response.data;
      if (response.data && responseType === 'object') {
        return response.data;
      }
      return false;
    } catch (error) {
      console.warn(`Initial Login call failed: ${error}`);
      return false;
    }
  }
  else{
    return false;
  }
};

const isUniversityUser = () => location.pathname.includes('/university');

export const logOut = (state, uid, accountId, universityUser) => {
  // logout from auth0 and redirect to login.
  sessionStorage.removeItem('errorCount');
  sessionStorage.removeItem('reduxState');
  sessionStorage.removeItem('backupReduxState');
  if (state && uid && accountId) {
    saveUerState(state, uid, accountId);
  }
  saveTrackingData({ loginCall: true });
  if (universityUser) {
    window.location.href = urls.AUTH_LOGUT_RETURN;
  } else {
    const webAuth = new auth0.WebAuth({
      domain: urls.AUTH_DOMAIN,
      clientID: urls.AUTH_CLIENTID,
    });
    webAuth.logout({
      returnTo: urls.AUTH_LOGUT_RETURN,
      client_id: urls.AUTH_CLIENTID,
    });
  }
};

const getDeepTreeCopy = dataTree => {
  return JSON.parse(dataTree);
};

export const getUser = () => async (dispatch, getState) => {
  // added async login call along with UserData, Account Details, Features List and Studies.
  const isUnlock =
    window.location.pathname === '/info' ||
    window.location.pathname === '/error' ||
    window.location.pathname === '/registration';
  let isLoginSuccess = false;
  const isUni = isUniversityUser();
  if (isUnlock) {
    isLoginSuccess = true;
  } else {
    const user = await getActiveUserDetail();
    let isValidUser = false;
    if (user) {
      const { universityUser, universityShortName } = user;
      if (universityUser && isUni) {
        const isUniMatched = location.pathname.includes(
          `/university/${universityShortName}`
        );
        if (
          location.pathname === '/university' ||
          location.pathname === '/university/' ||
          isUniMatched
        ) {
          const html = document.getElementsByTagName('html');
          html[0].classList.add('university-page');
          isValidUser = true;
        } else {
          isValidUser = false;
          logOut();
        }
      } else {
        isValidUser = true;
      }
    }

    if (isValidUser) {
      isLoginSuccess = true;
      dispatch(
        loginSuccess({
          data: user,
        })
      );
      new Promise(resolve => {
        dispatch({
          type: types.GET_USER_DETAILS,
          payload: user.permissions,
          allFeatures: user.features,
        });
        resolve(true);
      }).then(() => {
        if (_.isEmpty(sessionStorage.reduxState)) {
          const { universityUser } = user;
          const clonedPrefernces = getClonedPrefrences(
            user.preferences === null ? {} : user.preferences,
            getState
          );
          dispatch({
            type: types.SET_PREFERENCES,
            payload: clonedPrefernces,
          });
          if (!universityUser) {
            dispatch({
              type: types.GET_LATEST_STUDIES,
              payload: user.latestStudies,
            });
          }
          dispatch(setModuleDataOnSave(clonedPrefernces));
        } else {
          const backUpReduxState = _.cloneDeep(sessionStorage.reduxState);
          sessionStorage.removeItem('reduxState');
          sessionStorage.setItem('backupReduxState', backUpReduxState);
          isLoginSuccess = true;
        }
      });
      const sortedAccouts = _.sortBy(user.accounts, 'accountName');
      let newSortedAccount = [];
      const allAccountIds = _.map(sortedAccouts, item => item.accountId);
      if (
        !_.isEmpty(user.attributes) &&
        user.attributes.preferred_account_id &&
        allAccountIds.includes(Number(user.attributes.preferred_account_id))
      ) {
        const perferedAccountData = _.filter(
          sortedAccouts,
          item =>
            item.accountId === Number(user.attributes.preferred_account_id)
        );
        newSortedAccount = [perferedAccountData[0]];
        _.map(sortedAccouts, item => {
          if (item.accountId !== Number(user.attributes.preferred_account_id)) {
            newSortedAccount.push(item);
          }
        });
        dispatch(setPerferedAccount(user.attributes.preferred_account_id));
      } else {
        newSortedAccount = sortedAccouts;
      }
      dispatch(setAccountDetail(newSortedAccount));
      dispatch(setActiveAccount(user.currentAccountId));
    } else if (isUni) {
      const html = document.getElementsByTagName('html');
      html[0].classList.add('university-page');
      isLoginSuccess = true;
    } else {
      isLoginSuccess = false;
    }
  }
  return isLoginSuccess;
};

export const switchAccount = (accountId, accountName) => {
  const url = `${urls.API_GATEWAY_URL}/account/switch?accountId=${accountId}&accountName=${accountName}`;
  axios({
    url,
    method: 'get',
    withCredentials: true,
  })
    .then(response => {
      if (response.data === 'success') {
        window.location.href = '/';
      } else {
        toastError(response.data);
      }
    })
    .catch(error => {
      console.warn(`Initial Study call failed: ${error}`);
      toastError('Something wrong with Switch Account.');
    });
};
export const impersonatUser = userName => {
  const url = `${urls.API_GATEWAY_URL}/impersonate?username=${userName}`;
  axios({
    url,
    method: 'get',
    withCredentials: true,
  })
    .then(response => {
      if (response.data === 'success') {
        // window.location.reload();
        window.location.href = '/';
      } else {
        toastError(response.data);
      }
    })
    .catch(error => {
      console.warn(`Impersonate call failed: ${error}`);
      toastError(error.response.data.error);
    });
};
export const exitImpersonate = () => {
  const url = `${urls.API_GATEWAY_URL}/exit/impersonation`;
  axios({
    url,
    method: 'get',
    withCredentials: true,
  })
    .then(response => {
      if (response.data === 'success') {
        window.location.href = '/';
      } else {
        toastError(response.data);
      }
    })
    .catch(error => {
      console.warn(`Exit impersonation call failed: ${error}`);
      toastError(error.message);
    });
};
export const searchUsers = user => {
  const url = `${urls.API_GATEWAY_URL}/api/user-service/user/search?q=${user}`;
  return axios({
    url,
    method: 'get',
    withCredentials: true,
  })
    .then(response => {
      if (response.data) {
        return response.data;
      }
      return null;
    })
    .catch(error => {
      console.warn(`search user call failed: ${error}`);
      toastError(error.message);
    });
};

// Thunks - actions that return a function that accepts a dispatch
export const changePassword = async (username, password, newPassword) => {
  const url = `${urls.API_GATEWAY_URL}${urls.USER_SERVICE_URL}/user/changepassword`;
  try {
    const response = await axios({
      url,
      method: 'post',
      responseType: 'json',
      data: {
        username,
        password,
        newPassword,
      },
      withCredentials: true,
    });
    // if (response.data === 'SUCCESS') {
    return response.data;
    // }
  } catch (error) {
    console.warn(`Change Password call failed: ${error}`);
    toastError(error.message);
    return false;
  }
};

export const perferedAccount = accountId => async dispatch => {
  const url = `${urls.API_GATEWAY_URL}/account/preferred`;
  try {
    const response = await axios({
      url,
      method: 'post',
      // responseType: 'json',
      data: { attributes: { preferred_account_id: accountId } },
      withCredentials: true,
    });
    // console.log(response);
    if (response.data === 'success') {
      dispatch(setPerferedAccount(accountId));
    }
    return true;
  } catch (error) {
    console.warn(`Preffered account call failed: ${error}`);
    toastError(error.message);
    return false;
  }
};
const getUserStatus = async () => {
  const statusUrl = `${urls.API_GATEWAY_URL}/user/status`;
  try {
    return await axios({
      url: statusUrl,
      method: 'get',
      withCredentials: true,
      responseType: 'text',
    });    
  }
  catch(error){
    console.warn(`User status call failed: ${error}`);
    return false;
  }
};

export const acceptTerms = async () => {
  const url = `${urls.API_GATEWAY_URL}${urls.USER_SERVICE_URL}/user/acceptterms`;
  try {
    return await axios({
      url,
      method: 'post',
      withCredentials: true
    });
  }
  catch (error){
    console.warn(`User accept terms failed: ${error}`);
    return false;
  }
};
